import { Injectable } from "@angular/core";
import { from, Observable, of } from "rxjs";
import { catchError, delay, map } from "rxjs/operators";
import * as fakerRoot from "@faker-js/faker";

const faker = fakerRoot.faker;
import { FoodItemDTO, FoodService } from "./food.service.base";
import { FoodOrderService, ItemStatuses } from "./food-order.service.base";
import {
  FoodItemAvailability,
  FoodOrderHistory,
  FoodOrderShallow,
  NotificationStatus,
  OrderReview,
  OrderStatuses,
  RestaurantNotification,
} from "src/app/pages/merchant/food-order/models";
import {
  AdjustmentDTO,
  FoodCustomisationSetting,
  ItemAdjustmentType,
  ModifierSetting,
  OrderDetail,
  OrderItem,
} from "src/app/pages/merchant/food-order/models/food-order.detail";
import { HttpRequestsService } from "../http-requests.service";
import { environment } from "src/environments/environment";
import { match } from "ts-pattern";
import { HttpParams } from "@angular/common/http";
import { MERCHANT_ROUTES } from "src/app/constants/constants";

@Injectable({
  providedIn: "root",
})
export class FoodOrderServiceApi implements FoodOrderService {
  private foodItemsByCategory: { [key: string]: FoodItemAvailability[] } = {}; // Store fake items by category ID
  private foodOrders: FoodOrderShallow[] = [];
  private foodOrderServiceHost: string = `${environment.FOOD_SERVICE}foods/order/restaurant/`;
  private restaurantServiceHost: string = `${environment.FOOD_SERVICE}foods/restaurant/`;
  private foodServiceHost: string = environment.FOOD_SERVICE;
  private reviews: OrderReview[] = [];
  constructor(
    private foodService: FoodService,
    private http: HttpRequestsService,
  ) {
    this.generateFakeDataFromCategories();
    this.generateMockReviews(20);
  }

  private generateFakeDataFromCategories(): void {
    this.foodService.getCategoryList().subscribe((categories) => {
      categories.forEach((category) => {
        this.foodItemsByCategory[category.id] = this.generateFakeFoodItems();
      });
    });
  }

  private generateMockReviews(count: number): void {
    for (let i = 0; i < count; i++) {
      this.reviews.push(this.createMockReview());
    }
  }

  private createMockReview(): OrderReview {
    return {
      id: faker.string.uuid(),
      orderId: "38a3ef9c-46f4-4881-bca8-6e7244262fbf",
      customerName: faker.person.fullName(),
      customerId: faker.string.uuid(),
      customerProfileImage: faker.image.avatar(),
      content: faker.datatype.boolean() ? faker.lorem.paragraph() : undefined,
      replyContent: faker.datatype.boolean()
        ? faker.lorem.sentence()
        : undefined,
      rating: faker.number.int({ min: 1, max: 5 }),
      orderTotal: faker.commerce.price(),
      orderPlacingDate: faker.date.past(),
      totalLike: faker.number.int({ min: 0, max: 100 }),
      totalDislike: faker.number.int({ min: 0, max: 20 }),
      userTotalOrderFinished: faker.number.int({ min: 1, max: 50 }),
    };
  }

  private generateFakeFoodItems(): FoodItemAvailability[] {
    const items: FoodItemAvailability[] = [];
    for (let i = 0; i < 20; i++) {
      items.push({
        id: faker.string.uuid(),
        name: faker.food.dish(),
        profileImageUrl: faker.image.urlLoremFlickr({
          width: 480,
          height: 480,
          category: "food",
        }),
        isAvailable: faker.datatype.boolean(),
      });
    }
    return items;
  }

  getItemAvaiList(
    page: number,
    limit: number,
    catIds?: string[],
    search?: string,
    isSoldOut?: boolean,
  ): Observable<{
    meta: {
      total: number;
      soldOutTotal: number;
    };
    data: FoodItemAvailability[];
  }> {
    let httpParams = new HttpParams()
      .set("page", page.toString())
      .set("limit", limit.toString());

    if (catIds) {
      httpParams = catIds.reduce(
        (params, catId, index) => params.append(`categoryIds[${index}]`, catId),
        httpParams,
      );
    }

    if (search) {
      httpParams = httpParams.set("search", search);
    }

    if (isSoldOut) {
      httpParams = httpParams.set("isSoldOut", isSoldOut);
    }

    const url = `${MERCHANT_ROUTES.MENU_ITEM}?${httpParams.toString()}`;

    return from(
      this.http.rawGet<{
        meta: {
          total: number;
          page: number;
          limit: number;
          countSoldOut: number;
        };
        data: FoodItemDTO[];
      }>(this.foodServiceHost, url),
    ).pipe(
      map((response) => ({
        meta: {
          total: response.meta.total,
          soldOutTotal: response.meta.countSoldOut,
        },
        data: response.data.map((dto) => {
          return {
            name: dto.name,
            id: dto.id,
            profileImageUrl: dto.profileImage,
            isAvailable: !dto.outOfStock,
            unavailableUtilTo: dto.outOfStockUntilTo,
          } as FoodItemAvailability;
        }),
      })),
      catchError((error) => {
        console.error("Error getting menu items:", error);
        throw error;
      }),
    );
  }
  updateItemAvailability(
    itemId: string,
    status: ItemStatuses,
    utilTo?: Date,
  ): Observable<void> {
    return from(
      this.http.rawPut(
        this.foodServiceHost,
        MERCHANT_ROUTES.MENU_ITEM_STOCK.replace(":id", itemId),
        {
          outOfStock:
            status === ItemStatuses.soldOut ||
            status === ItemStatuses.soldOutUtilTmrw,
          untilTo: utilTo,
        },
      ),
    );
  }

  getOrderList(
    limit: number,
    cursor: string | null,
    filter: {
      statuses: OrderStatuses[];
      dateRange?: {
        start: Date;
        end: Date;
      };
      search?: string;
    },
    endpoint: string = "",
  ): Observable<{
    data: FoodOrderShallow[];
    nextCursor: string | null;
  }> {
    const params: string[] = [`limit=${limit}`];

    if (cursor) {
      params.push(`cursor=${cursor}`);
    }
    if (filter.statuses?.length > 0) {
      params.push(`statuses=${filter.statuses.join(",")}`);
    }
    if (filter.dateRange) {
      params.push(`startDate=${filter.dateRange.start.toISOString()}`);
      params.push(`endDate=${filter.dateRange.end.toISOString()}`);
    }
    if (filter.search) {
      params.push(`search=${filter.search}`);
    }

    const fullUrl = `${endpoint}?${params.join("&")}`;

    return from(this.http.rawGet(this.foodOrderServiceHost, fullUrl)).pipe(
      map((response) => ({
        data: response.data.map(this.mapToFoodOrderShallow),
        nextCursor: response.nextCursor,
      })),
    );
  }

  getOrderLists(
    limit: number,
    statuses: OrderStatuses[],
    filter: {
      dateRange?: {
        start: Date;
        end: Date;
      };
      search?: string;
    },
  ): Observable<
    Record<
      string,
      {
        data: FoodOrderShallow[];
        nextCursor: string | null;
      }
    >
  > {
    const params: string[] = [`limit=${limit}`];

    if (statuses?.length > 0) {
      params.push(`statuses=${statuses.join(",")}`);
    }
    if (filter.dateRange) {
      params.push(`startDate=${filter.dateRange.start.toISOString()}`);
      params.push(`endDate=${filter.dateRange.end.toISOString()}`);
    }
    if (filter.search) {
      params.push(`search=${filter.search}`);
    }

    const fullUrl = `overview-lists?${params.join("&")}`;

    return from(this.http.rawGet(this.foodOrderServiceHost, fullUrl)).pipe(
      map((response) =>
        Object.entries(response).reduce((acc, statusList) => {
          return {
            ...acc,
            [statusList[0]]: {
              data: (statusList[1] as any).data.map(this.mapToFoodOrderShallow),
              nextCursor: (statusList[1] as any).nextCursor,
            },
          };
        }, {}),
      ),
    );
  }

  getHistoryOrder(
    limit: number,
    cursor: string | null,
    filter: {
      statuses: OrderStatuses[];
      dateRange?: {
        start: Date;
        end: Date;
      };
      search?: string;
    },
  ): Observable<{
    data: FoodOrderHistory[];
    nextCursor: string | null;
  }> {
    const params: string[] = [`limit=${limit}`];

    if (cursor) {
      params.push(`cursor=${cursor}`);
    }
    if (filter.statuses?.length > 0) {
      params.push(`statuses=${filter.statuses.join(",")}`);
    }
    if (filter.dateRange) {
      params.push(`startDate=${filter.dateRange.start.toISOString()}`);
      params.push(`endDate=${filter.dateRange.end.toISOString()}`);
    }
    if (filter.search) {
      params.push(`search=${filter.search}`);
    }

    const fullUrl = `${"order-history"}?${params.join("&")}`;

    return from(this.http.rawGet(this.foodOrderServiceHost, fullUrl)).pipe(
      map((response) => ({
        data: response.data.map(this.mapToFoodOrderHistory),
        nextCursor: response.nextCursor,
      })),
    );
  }
  private mapToFoodOrderHistory(orderHistory: any) {
    return {
      ...orderHistory,
      orderPrice: parseFloat(orderHistory.orderPrice),
    } as FoodOrderHistory;
  }
  mapToFoodOrderShallow(order: any): FoodOrderShallow {
    const estimatedReadyTime = new Date(order.estimatedReadyTime);
    const readyForAcceptedAt = new Date(order.readyForAcceptedAt);
    const DURATION_ACCEPT_TIME = 5 * 60 * 1000;
    const remainingDurationAcceptTime =
      DURATION_ACCEPT_TIME - (Date.now() - readyForAcceptedAt.getTime()); // in seconds;
    return {
      id: order.orderId,
      customer: {
        name: order.customerName,
        id: order.customerId,
      },
      estimatedReadyAt: estimatedReadyTime,
      readyForAcceptedAt: readyForAcceptedAt,
      readyRemainingTime: estimatedReadyTime.getTime() - Date.now(),
      acceptRemainingTime: remainingDurationAcceptTime,
      totalPrice: parseFloat(order.totalPrice),
      finalPrice: parseFloat(order.finalPrice),
      status: order.orderStatus || order.status,
      deliveryAssignmentDriverStatus: order.deliveryAssignmentDriverStatus,
      numberOfItems: order.numberOfItems,
      isPickup: order.isPickup,
      no: order.orderNo,
    };
  }
  markOrderReady(orderId: string): Observable<void> {
    return from(
      this.http.rawPut(this.foodOrderServiceHost, `orders/${orderId}/status`, {
        status: OrderStatuses.READY_FOR_PICKUP,
      }),
    );
  }

  markOrderPreparing(orderId: string): Observable<void> {
    return from(
      this.http.rawPut(this.foodOrderServiceHost, `orders/${orderId}/status`, {
        status: OrderStatuses.PREPARING,
      }),
    );
  }

  getOrderDetail(id: string): Observable<OrderDetail> {
    return from(
      this.http.rawGet(this.foodOrderServiceHost, `orders/${id}`),
    ).pipe(map((response) => this.mapToOrderDetail(response.data)));
  }

  private mapToOrderDetail(dto: any): OrderDetail {
    return {
      id: dto.id,
      totalAllergiesRequest: dto.totalAllergiesRequest,
      orderNum: dto.orderNum,
      customerName: dto.customerName,
      customerAvatar: dto.customerAvatar,
      customerId: dto.customerId,
      numberOfItems: dto.numberOfItems,
      items: dto.items.map(this.mapToOrderItem.bind(this)),
      specialInstructions: dto.specialInstructions,
      estimatedReadyTime: new Date(dto.estimatedReadyTime),
      hasEstTimeUpdated: dto.hasReadyTimeUpdated,
      customerCancelReason: dto.customerCancelReason,
      restaurantCancelReason: dto.restaurantCancelReason,
      finalPrice: parseFloat(dto.finalPrice),
      subTotal: parseFloat(dto.subTotal),
      originalPrice: parseFloat(dto.originalPrice),
      isPickup: dto.isPickup,
      vat: parseFloat(dto.vat),
      deliveryAssignmentDriverStatus: dto.deliveryAssignmentDriverStatus,
      priceAdjustment: dto.priceAdjustment
        ? {
            amount: parseFloat(dto.priceAdjustment.amount),
            reason: dto.priceAdjustment.reason,
          }
        : undefined,
      createdAt: new Date(dto.createdAt),
      status: dto.status,
    };
  }

  private mapToOrderItem(dto: any): OrderItem {
    return {
      id: dto.id,
      itemName: dto.itemName,
      amount: dto.amount,
      totalPrice: parseFloat(dto.totalPrice),
      specialInstructions: dto.specialInstructions,
      allergies: dto.allergies,
      modifierGroups: dto.modifierGroups.map(
        this.mapToModifierGroup.bind(this),
      ),
      adjustments: dto.adjustments.map(this.mapToAdjustment),
      basePrice: parseFloat(dto.basePrice),
    };
  }

  private mapToModifierGroup(dto: any): FoodCustomisationSetting {
    return {
      name: dto.name,
      modifiers: dto.modifiers.map(this.mapToModifier),
      totalPrice: parseFloat(dto.totalPrice),
    };
  }

  private mapToModifier(dto: any): ModifierSetting {
    return {
      name: dto.name,
      amount: dto.name,
      price: dto.price,
      totalPrice: parseFloat(dto.totalPrice),
    };
  }

  private mapToAdjustment(dto: any): AdjustmentDTO {
    return {
      type: match(dto.type)
        .with("ITEM_OUT_OF_STOCK", () => ItemAdjustmentType.OUT_OF_STOCK)
        .otherwise(() => "") as ItemAdjustmentType,
      priceChange: dto.priceChange,
      quantityChange: dto.quantityChange,
      reason: dto.reason,
    };
  }

  updateOrderItemAdjustment(
    orderId: string,
    orderItemId: string,
    adjustments: AdjustmentDTO[],
  ): Observable<void> {
    return from(
      this.http.rawPut(
        this.foodOrderServiceHost,
        `orders/${orderId}/adjustments`,
        {
          adjustments: adjustments.map((adjust) => {
            return {
              orderItemId: orderItemId,
              adjustmentType: this.mapAdjustmentType(adjust.type),
              reason: adjust.reason,
            };
          }),
        },
        {},
        false,
        false,
      ),
    );
  }
  private mapAdjustmentType(type: ItemAdjustmentType): string {
    switch (type) {
      case ItemAdjustmentType.OUT_OF_STOCK_UNTIL_TMRW:
        return "ITEM_OUT_OF_STOCK_UNTIL_TMRW";
      case ItemAdjustmentType.OUT_OF_STOCK:
        return "ITEM_OUT_OF_STOCK";
      case ItemAdjustmentType.IN_STOCK:
        return "ITEM_IN_STOCK";
      default:
        throw new Error(`Unsupported adjustment type: ${type}`);
    }
  }

  notifyCustomerModifier(orderId) {
    return of(undefined).pipe(delay(1000));
  }

  updatePrice(
    orderId: string,
    amount: number,
    // reason?: string,
  ): Observable<void> {
    return from(
      this.http.rawPut(
        this.foodOrderServiceHost,
        `orders/${orderId}/price-adjustment`,
        {
          amount,
        },
      ),
    );
  }
  // readyTimein in second
  updateReadyTime(orderId: string, newReadyTime: Date): Observable<Date> {
    return from(
      this.http.rawPut(
        this.foodOrderServiceHost,
        `orders/${orderId}/ready-time`,
        {
          readyTime: newReadyTime.toISOString(),
        },
        {},
        false,
        false,
      ),
    );
  }

  updatePreparingTime(orderId: string, duration: number) {
    return from(
      this.http.rawPut(
        this.foodOrderServiceHost,
        `orders/${orderId}/preparing-time`,
        {
          duration,
        },
        {},
        false,
        false,
      ),
    );
  }

  cancelOrder(orderId: string, reason: string): Observable<void> {
    return from(
      this.http.rawPut(
        this.foodOrderServiceHost,
        `orders/${orderId}/status`,
        {
          status: "CANCELLED",
          reason,
        },
        {},
        false,
        false,
      ),
    );
  }

  acceptOrder(
    orderId: string,
    estimatedPreparingTime: number,
  ): Observable<void> {
    return from(
      this.http.rawPut(
        this.foodOrderServiceHost,
        `orders/${orderId}/status`,
        {
          status: OrderStatuses.PREPARING,
          estimatedPreparingTime,
        },
        {},
        false,
        false,
      ),
    );
  }

  completeOrder(orderId: string): Observable<void> {
    return from(
      this.http.rawPut(
        this.foodOrderServiceHost,
        `orders/${orderId}/status`,
        {
          status: OrderStatuses.COMPLETED,
        },
        {},
        false,
        false,
      ),
    );
  }

  rejectOrder(orderId: string): Observable<void> {
    return from(
      this.http.rawPut(
        this.foodOrderServiceHost,
        `orders/${orderId}/status`,
        {
          status: "REJECTED",
        },
        {},
        false,
        false,
      ),
    );
  }
  /* fake */
  // getListReview(params): Observable<{
  //   data: OrderReview[];
  //   nextCursor: string;
  //   total: number;
  // }> {
  //   const pageSize = 10;
  //   const data = this.reviews.slice(0, pageSize);
  //   const nextCursor =
  //     this.reviews.length > pageSize ? "next_page_token" : null;

  //   return of({ data, nextCursor, total: 500 }).pipe(delay(500));
  // }

  getListReview(params): Observable<{
    data: OrderReview[];
    nextCursor: string | null;
    total: number;
  }> {
    let httpParams = new HttpParams();
    if (params.ratings) {
      httpParams = httpParams.set("ratings", JSON.stringify(params.ratings));
    }

    if (params.nextCursor) {
      httpParams = httpParams.set("nextCursor", params.nextCursor);
    }

    if (params.hasContent != undefined) {
      httpParams = httpParams.set("hasContent", params.hasContent);
    }

    if (params.hasReply != undefined) {
      httpParams = httpParams.set("hasReply", params.hasReply);
    }

    const url = `${MERCHANT_ROUTES.REVIEW}?${httpParams.toString()}`;
    return from(this.http.rawGet(this.restaurantServiceHost, url)).pipe(
      map((data) => ({
        total: data.total,
        nextCursor: data.nextCursor,
        data: data.data.map((rawReview) => ({
          id: rawReview.id,
          orderId: rawReview.orderId,
          customerName: rawReview.userName,
          customerId: rawReview.userId,
          customerProfileImage: rawReview.userAvatar,
          content: rawReview.content,
          replyContent: rawReview.replyContent,
          rating: rawReview.rating,
          orderTotal: rawReview.orderFinalPrice,
          orderPlacingDate: rawReview.orderDate,
          totalLike: rawReview.likeCount,
          totalDislike: rawReview.unlikeCount,
          userTotalOrderFinished: rawReview.userTotalOrders,
        })),
      })),
    );
  }
  // replyReview(
  //   orderId: string,
  //   content: string,
  //   selectDiscount: number,
  // ): Observable<void> {
  //   return of(null).pipe(delay(3000));
  // }
  //

  replyReview(
    reviewId: string,
    content: string,
    selectDiscount: number,
  ): Observable<void> {
    const url = `${MERCHANT_ROUTES.REVIEW}/${reviewId}/reply`;
    return from(
      this.http.rawPost(this.restaurantServiceHost, url, {
        replyContent: content,
      }),
    ).pipe(map(() => null));
  }

  getDiscountSetting(): Observable<{ orderSuggestAmounts: number[] }> {
    return of({
      orderSuggestAmounts: [5, 10, 15, 20],
    }).pipe(delay(3000));
  }

  getNotifications(params: {
    nextCursor?: string;
    limit: number;
    status: NotificationStatus;
  }): Observable<{
    data: RestaurantNotification[];
    nextCursor: string;
    total: number;
  }> {
    let httpParams = new HttpParams();

    if (params.nextCursor) {
      httpParams = httpParams.set("nextCursor", params.nextCursor);
    }
    if (params.limit) {
      httpParams = httpParams.set("limit", params.limit);
    }

    if (params.status) {
      httpParams = httpParams.set("status", params.status);
    }
    const url = `${MERCHANT_ROUTES.FOOD_NOTIFICATIONS}?${httpParams.toString()}`;
    return from(this.http.rawGet(this.restaurantServiceHost, url)).pipe(
      map((data) => ({
        nextCursor: data.nextCursor,
        total: data.total,
        data: (data.items || []).map((item) => {
          return {
            id: item.id,
            restaurantId: item.restaurantId,
            title: item.title,
            message: item.message,
            type: item.type,
            data: item.data,
            status: item.status,
            createdAt: item.createdAt,
          };
        }),
      })),
    );
  }

  markNotificationAsRead(notificationId: string): Observable<void> {
    const url = `${MERCHANT_ROUTES.FOOD_NOTIFICATIONS}/${notificationId}/status`;
    return from(
      this.http.rawPatch(this.restaurantServiceHost, url, {
        status: NotificationStatus.READ,
      }),
    );
  }
}
